import { useState, useEffect, useRef } from 'react'
import { asMessage } from '../../../shared/utils/errorDetail'
import { identityService } from '../../../shared/api'
import { useTenantBranding } from '../../../contexts/TenantContext'
import { applyBrandVars } from '../../../shared/brandCss'
import { Palette, Upload, Save, CheckCircle2, AlertCircle, Image as ImageIcon } from 'lucide-react'

const NEUTRAL_PRIMARY = '#334155'
const NEUTRAL_SECONDARY = '#475569'

const LOGO_VARIANTS = [
  { type: 'dark', label: 'Login / header logo', hint: 'Shown on the login page + dark headers (use a light/white logo).', bg: 'bg-gray-800' },
  { type: 'light', label: 'Nav-bar logo', hint: 'Shown in the top navigation (use a dark logo on white).', bg: 'bg-white border border-gray-200' },
  // THE APP ICON. There was no way to set this, which is why every tenant kept the placeholder in
  // the browser tab and on the home screen. Neither logo above can stand in: the login logo is
  // white by design and disappears on a light tab strip, and the nav-bar logo is a wide wordmark
  // that turns to mush at 32px. Say "square" in the hint, because that is the part people get wrong
  // and the one thing we cannot fix for them.
  { type: 'icon', label: 'App icon', hint: 'The browser tab, and the icon on a phone once someone installs the app. Use a SQUARE image — a simple mark, not the full wordmark.', bg: 'bg-white border border-gray-200' },
]

/**
 * Per-tenant brand editor — the "3-way switch". Both mount points write the SAME
 * identity_tenant_settings + app_logos records via the SAME endpoints; last write wins.
 *   mode='super'  → system_admin edits ANY tenant (backend routes writes through the owner session).
 *   mode='tenant' → master_admin edits its OWN tenant (backend writes on the request session, RLS-clamped).
 */
export default function TenantBrandEditor({ tenantId: tenantIdProp, mode = 'tenant', onSaved }) {
  const tenant = useTenantBranding()
  const isSuper = mode === 'super'
  const tenantId = tenantIdProp || (isSuper ? null : tenant?.tenantId)

  const [form, setForm] = useState({ name: '', primary_color: NEUTRAL_PRIMARY, secondary_color: NEUTRAL_SECONDARY })
  const [domain, setDomain] = useState(isSuper ? '' : (typeof window !== 'undefined' ? window.location.hostname : ''))
  const [loading, setLoading] = useState(isSuper)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState({})
  const [msg, setMsg] = useState(null) // { type: 'ok' | 'err', text }
  const [logoBust, setLogoBust] = useState(0)
  const previewRef = useRef(null)
  // ONE ref object keyed by variant, populated BY THE RENDER via callback refs — not a hand-written
  // map of individual useRef()s. The hand-written version listed `light` and `dark` only, so adding
  // the App icon left `fileRefs.icon` undefined and its Upload button threw
  // "Cannot read properties of undefined (reading 'current')" on click — the crash happened in the
  // handler, so no request was ever made and no error surfaced in the UI. Deriving the map from
  // whatever LOGO_VARIANTS renders means a fourth slot cannot desync it again.
  const fileRefs = useRef({})

  // ── Load current values ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (isSuper) {
        if (!tenantId) return
        try {
          setLoading(true)
          const d = await identityService.getBrandingAdmin(tenantId)
          if (cancelled) return
          setForm({
            name: d.name || '',
            primary_color: d.primary_color || NEUTRAL_PRIMARY,
            secondary_color: d.secondary_color || NEUTRAL_SECONDARY,
          })
          setDomain(d.custom_domain || d.subdomain || '')
        } catch (e) {
          if (!cancelled) setMsg({ type: 'err', text: 'Failed to load tenant branding.' })
        } finally {
          if (!cancelled) setLoading(false)
        }
      } else if (tenant) {
        setForm({
          name: tenant.name || '',
          primary_color: tenant.primaryColor || NEUTRAL_PRIMARY,
          secondary_color: tenant.secondaryColor || NEUTRAL_SECONDARY,
        })
        setDomain(window.location.hostname)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, tenantId, tenant?.tenantId])

  // ── Live preview ─────────────────────────────────────────────────────────────
  // tenant mode paints the whole app (it IS the tenant's host); super mode paints only
  // the scoped preview box, so the admin's own chrome is never repainted.
  useEffect(() => {
    if (isSuper) {
      if (previewRef.current) applyBrandVars(previewRef.current.style, form.primary_color, form.secondary_color)
    } else {
      applyBrandVars(document.documentElement.style, form.primary_color, form.secondary_color)
    }
  }, [isSuper, form.primary_color, form.secondary_color])

  const logoUrl = (type) => {
    const base = isSuper
      ? identityService.getAppLogoUrlForDomain(type, domain)
      : identityService.getAppLogoUrl(type)
    return `${base}&t=${logoBust}`
  }

  const handleLogo = async (type, file) => {
    if (!file) return
    setUploading((p) => ({ ...p, [type]: true }))
    setMsg(null)
    try {
      if (isSuper) await identityService.uploadAppLogoForTenant(tenantId, type, file)
      else await identityService.uploadAppLogo(type, file)
      setLogoBust(Date.now())
      const label = LOGO_VARIANTS.find((v) => v.type === type)?.label || type
      setMsg({ type: 'ok', text: `${label} updated.` })
      if (!isSuper && tenant?.refresh) tenant.refresh()
    } catch (e) {
      setMsg({ type: 'err', text: asMessage(e?.response?.data?.detail) || `Failed to upload ${type} logo.` })
    } finally {
      setUploading((p) => ({ ...p, [type]: false }))
    }
  }

  const handleSave = async () => {
    if (!tenantId) { setMsg({ type: 'err', text: 'No tenant selected to save.' }); return }
    setSaving(true)
    setMsg(null)
    try {
      await identityService.updateBranding(tenantId, {
        name: form.name,
        primary_color: form.primary_color,
        secondary_color: form.secondary_color,
      })
      setMsg({ type: 'ok', text: 'Branding saved.' })
      if (isSuper) { if (onSaved) onSaved() }
      else if (tenant?.refresh) tenant.refresh()
    } catch (e) {
      // A 422's detail is a LIST: as text, or the Super Admin page goes blank (review SA-1).
      setMsg({ type: 'err', text: asMessage(e?.response?.data?.detail) || 'Failed to save branding.' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading branding…</div>
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-100 text-slate-700 rounded-lg"><Palette className="w-5 h-5" /></div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Branding{form.name ? ` — ${form.name}` : ''}</h2>
          <p className="text-sm text-gray-500">
            {isSuper ? 'Set this tenant’s logo and colors.' : 'Set your organization’s logo and colors.'}
            {domain ? <span className="text-gray-400"> ({domain})</span> : null}
          </p>
        </div>
      </div>

      {msg && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${msg.type === 'ok' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {msg.type === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colors */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              placeholder="e.g. Acme Corp"
            />
          </div>
          {[['primary_color', 'Primary color'], ['secondary_color', 'Secondary color']].map(([key, label]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="h-10 w-16 p-1 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={form[key]}
                  maxLength={7}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
                />
              </div>
            </div>
          ))}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-900 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save colors'}
          </button>
        </div>

        {/* Live preview */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Preview</p>
          <div
            ref={previewRef}
            className="rounded-lg overflow-hidden border border-gray-200"
          >
            <div className="py-6 px-4 text-center" style={{ backgroundColor: form.primary_color }}>
              <img
                src={logoUrl('dark')}
                alt="logo preview"
                className="h-14 mx-auto object-contain"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <p className="text-white font-semibold mt-2">{form.name || 'Your Portal'}</p>
            </div>
            <div className="p-4 bg-gray-50 flex items-center gap-2">
              <span className="text-xs text-gray-500">Accent</span>
              <span className="inline-block w-6 h-6 rounded" style={{ backgroundColor: form.secondary_color }} />
              <button
                className="ml-auto text-xs font-medium text-white px-3 py-1.5 rounded"
                style={{ backgroundColor: form.primary_color }}
              >
                Sample button
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logos */}
      <div className="border-t border-gray-100 pt-6">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon className="w-4 h-4 text-slate-600" />
          <h3 className="font-semibold text-gray-800 text-sm">Logos</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {LOGO_VARIANTS.map(({ type, label, hint, bg }) => (
            <div key={type} className="space-y-2">
              <p className="text-sm font-medium text-gray-700">{label}</p>
              <div className={`rounded-lg h-24 flex items-center justify-center overflow-hidden ${bg}`}>
                <img
                  src={logoUrl(type)}
                  alt={label}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              </div>
              <p className="text-xs text-gray-500">{hint}</p>
              <input
                ref={(el) => { fileRefs.current[type] = el }}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { handleLogo(type, e.target.files?.[0]); e.target.value = '' }}
              />
              <button
                onClick={() => fileRefs.current[type]?.click()}
                disabled={!!uploading[type]}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                {uploading[type] ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-3">PNG, JPG, or WebP · max 10 MB. Changes apply immediately.</p>
      </div>
    </div>
  )
}
